/**
 * Utility functions for invoice management business logic
 */

import { fileStatuses } from 'features/constants';

/**
 * Determines if the reopen file button should be disabled
 */
export function shouldDisableReopenButton(params: {
  fileStatus?: string;
  grossAmount?: number;
  isCheckingFileExistence: boolean;
  isCreatingFile: boolean;
  invoiceDetailsLoading: boolean;
}): boolean {
  const { fileStatus, grossAmount, isCheckingFileExistence, isCreatingFile, invoiceDetailsLoading } = params;

  // Disable if any loading state is active
  if (isCheckingFileExistence || isCreatingFile || invoiceDetailsLoading) {
    return true;
  }

  // Disable if file is already open
  if (fileStatus === fileStatuses.open) {
    return true;
  }

  // Disable if gross amount is negative (credit memo)
  if (grossAmount !== undefined && grossAmount < 0) {
    return true;
  }

  return false;
}

/**
 * Gets the appropriate tooltip message for the reopen button
 */
export function getReopenButtonTooltip(params: {
  fileStatus?: string;
  grossAmount?: number;
  isCheckingFileExistence: boolean;
  isCreatingFile: boolean;
}): string {
  const { fileStatus, grossAmount, isCheckingFileExistence, isCreatingFile } = params;

  if (fileStatus === fileStatuses.open) {
    return 'File is already open';
  }

  if (grossAmount !== undefined && grossAmount < 0) {
    return 'Cannot reopen file with credit memo invoice';
  }

  if (isCheckingFileExistence) {
    return 'Checking if file exists...';
  }

  if (isCreatingFile) {
    return 'Creating file...';
  }

  return 'Reopen file to make adjustment or correction';
}

/**
 * Validates if a file number matches the expected invoice file number
 * Logs a warning if there's a mismatch
 */
export function validateFileNumber(params: {
  fileId: string;
  expectedFileNumber: string;
  actualFileNumber?: string;
}): boolean {
  const { fileId, expectedFileNumber, actualFileNumber } = params;

  if (actualFileNumber !== expectedFileNumber) {
    console.warn(
      `File number mismatch for fileId ${fileId}: expected "${expectedFileNumber}", got "${actualFileNumber}"`,
    );
    return false;
  }

  return true;
}

/**
 * Determines if date filter should be applied to invoice search
 * Date filter is only applied when searching by firm alone (no specific search fields)
 */
export function shouldApplyDateFilter(params: {
  hasInvoiceNumber: boolean;
  hasPolicyNumber: boolean;
  hasOrderNumber: boolean;
  hasFileNumber: boolean;
  hasFirm: boolean;
}): boolean {
  const { hasInvoiceNumber, hasPolicyNumber, hasOrderNumber, hasFileNumber, hasFirm } = params;

  const hasSpecificSearchField = hasInvoiceNumber || hasPolicyNumber || hasOrderNumber || hasFileNumber;

  // Only apply date filter when searching by firm alone
  return !hasSpecificSearchField && hasFirm;
}
